import { useEffect, useRef } from "react";

import { useNewBuild } from "./deployedBuild";

/** `now` for unattended screens; `when-idle` waits until nobody has typed or tapped for `IDLE_MS`. */
export type StaleReload = "now" | "when-idle";

const POLL_MS = 60_000;
const IDLE_MS = 60_000;
// Outlasts a CloudFront edge still serving the old index.html after a deploy, so a reload that lands on it can't loop.
const RETRY_AFTER_MS = 5 * 60_000;
// A check older than this is re-run before reloading, so an idle wait can't reload a tab that went offline meanwhile.
const FRESH_MS = 5_000;
const STORAGE_KEY = "brian.build-reloads";

// Keyed per target: one slot would let two edges disagreeing on index.html and version.json ping-pong a tab.
const reloadToward = (target: string) => {
  try {
    const now = Date.now();
    const raw: unknown = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) ?? "{}",
    );
    const recent = new Map(
      Object.entries(raw && typeof raw === "object" ? raw : {}).filter(
        ([, at]) => typeof at === "number" && now - at < RETRY_AFTER_MS,
      ),
    );
    if (recent.has(target)) return;
    recent.set(target, now);
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(recent)),
    );
  } catch {
    // No storage means no loop guard, so stay on the old build.
    return;
  }
  window.location.reload();
};

/**
 * Reloads onto a newly deployed build. Only acts on a fresh successful check, so a
 * screen never reloads into the browser's offline page.
 */
const useReloadWhenStale = (mode: StaleReload) => {
  const { newBuild, checkedAt, refetch } = useNewBuild(POLL_MS, mode === "now");
  const lastInputAt = useRef(0);

  useEffect(() => {
    if (mode !== "when-idle") return;
    const mark = () => {
      lastInputAt.current = Date.now();
    };
    mark();
    window.addEventListener("keydown", mark, true);
    window.addEventListener("pointerdown", mark, true);
    return () => {
      window.removeEventListener("keydown", mark, true);
      window.removeEventListener("pointerdown", mark, true);
    };
  }, [mode]);

  useEffect(() => {
    if (!newBuild) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = () => {
      const wait =
        mode === "when-idle" ? lastInputAt.current + IDLE_MS - Date.now() : 0;
      if (wait > 0) timer = setTimeout(attempt, wait);
      else if (Date.now() - checkedAt < FRESH_MS) reloadToward(newBuild);
      else void refetch({ cancelRefetch: false });
    };
    attempt();
    return () => clearTimeout(timer);
  }, [mode, newBuild, checkedAt, refetch]);
};

/** A leaf, so each poll re-renders only this rather than the screen. */
export const StaleReloader = ({ mode }: { mode: StaleReload }) => {
  useReloadWhenStale(mode);
  return null;
};
